import React from 'react';
import * as d3 from 'd3-shape';
import {regressionLinear} from 'd3-regression';
import classNames from 'classnames';

const DEFAULT_COLOR = '#cccccc';
const DEFAULT_LINE_WIDTH = 3;

const CLICK_TARGET_SIZE = 10;
const LINEAR_REGRESSION_POINT_MINIMUM = 5;

/**
 * Line Series that goes hand-in-hand with TrendChart.
 *
 * For a simpler LineSeries, look at the one in src/charts
 *
 * TODO rename this one and move into src/charts
 */
export default class LineSeries extends React.Component {
  constructor(props) {
    super(props);
    this.registerPopoverItem = this.registerPopoverItem.bind(this);

    this.lines = [];

    this.state = {
      hoveredDot: undefined,
      // keep track of whether we've started animating the lines
      // (so we only do it once),
      // and whether we've already done it - in order to preserve
      // opacity if we've switched this component off/on
      begunAnimatingLine: false,
      haveAnimatedLine: false,
    };

    this.makeCircleFor = this.makeCircleFor.bind(this);
    this.makeLineFor = this.makeLineFor.bind(this);
    this.tryAnimatingLines = this.tryAnimatingLines.bind(this);
  }

  componentDidMount() {
    this.tryAnimatingLines();
  }

  componentDidUpdate() {
    this.tryAnimatingLines();
  }

  tryAnimatingLines() {
    if (this.props.data && this.props.data.length > 0 && !this.state.begunAnimatingLine) {
      const path = this.line;

      this.setState({begunAnimatingLine: true}, () => {
        setTimeout(() => {
          // https://jakearchibald.com/2013/animated-line-drawing-svg/

          const length = path.getTotalLength();
          // Clear any previous transition
          path.style.transition = path.style.WebkitTransition = 'none';
          // Set up the starting positions
          path.style.strokeDasharray = length + ' ' + length;
          path.style.strokeDashoffset = length;
          // Trigger a layout so styles are calculated & the browser
          // picks up the starting position before animating
          path.getBoundingClientRect();
          // Define our transition
          path.style.transition = path.style.WebkitTransition = 'stroke-dashoffset 2s ease-in-out';
          // Go!
          path.style.strokeDashoffset = '0';
          path.style.opacity = '1';

          this.setState({haveAnimatedLine: true});
        }, 200);
      });
    }
  }

  registerPopoverItem(c, data) {
    const {popoverContainer, tooltipContent} = this.props;
    if (popoverContainer) {
      popoverContainer.registerPopoverItem(c, () => tooltipContent(data));
    }
  }

  makeCircleFor(indicatorOptions, xScale, d, yScale, lineWidth, color, i) {
    const {hoveredDot, haveAnimatedLines} = this.state;

    return (
      <g
        className={haveAnimatedLines && 'animated-item'}
        onMouseEnter={() => this.setState({hoveredDot: i})}
        onMouseLeave={() => this.setState({hoveredDot: undefined})}
        key={i}>
        <circle
          className={classNames('circle', hoveredDot === i && 'hovered-dot')}
          r={indicatorOptions.radius}
          cx={xScale(d.date)}
          cy={yScale(d.value)}
          strokeWidth={null}
          stroke={'#636363'}
          fill={'#919191'}
          key={d.date}
        />
        <rect
          height={CLICK_TARGET_SIZE}
          width={CLICK_TARGET_SIZE}
          x={xScale(d.date) - CLICK_TARGET_SIZE / 2}
          y={yScale(d.value) - CLICK_TARGET_SIZE / 2}
          opacity={0}
          ref={c => c && this.registerPopoverItem(c, d)}
          cursor={'pointer'}
        />
      </g>
    );
  }

  makeLineFor(path, color, lineWidth) {
    const {haveAnimatedLine} = this.state;

    return (
      <path
        key={(path ? path.substring(25) : '') + color}
        d={path}
        stroke={color || DEFAULT_COLOR}
        fill="none"
        strokeWidth={lineWidth || DEFAULT_LINE_WIDTH}
        cursor={'auto'}
        ref={i => (this.line = i)}
        opacity={haveAnimatedLine ? 1 : 0}
      />
    );
  }

  makeLinearRegression(data, xScale, yScale) {
    // dont draw unless we have enough data and they're not all the same value
    if (
      !data ||
      data.length < LINEAR_REGRESSION_POINT_MINIMUM ||
      data.every(i => i.value === data[0].value)
    )
      return null;

    const linearRegression = regressionLinear()
      .x(d => xScale(d.date))
      .y(d => yScale(d.value));

    const [start, end] = linearRegression(data);

    return (
      <line
        x1={start[0]}
        y1={start[1]}
        x2={end[0]}
        y2={end[1]}
        className={classNames(['guideline-path', 'animated-item'])}
      />
    );
  }

  render() {
    const {
      scales,
      data,
      color,
      className,
      lineWidth,
      indicatorOptions,
      makeLinearRegression,
      showDataPoints,
    } = this.props;
    const {xScale, yScale} = scales;

    // TODO
    // move this whole thing into ev-charts
    // extract the regressionStyle flag into a (series of?) components
    // this is bursting at the seams a bit

    const line = d3
      .line()
      .x(d => xScale(d.date))
      .y(d => yScale(d.value))
      .curve(d3.curveBasis);

    const path = line(data);
    const linePath = this.makeLineFor(path, color, lineWidth);
    const points =
      indicatorOptions &&
      showDataPoints &&
      data.map((d, i) => {
        if (d.value === null || d.value === undefined) return null;
        if (d.indicator === false) return null;

        return this.makeCircleFor(indicatorOptions, xScale, d, yScale, lineWidth, color, i);
      });

    return (
      <g className={className} clipPath={this.props.clipPath && `url(#${this.props.clipPath})`}>
        {makeLinearRegression && this.makeLinearRegression(data, xScale, yScale)}
        {[linePath, points]}
      </g>
    );
  }
}
